import React, { useState, useEffect, useMemo } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    CustomInput,
    Button,
    Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateTenant } from "api/tenant/service";
import { getActiveColorStyle } from "utils/colorUtils";
import { useTheme2FormPage, Theme2FormFieldsStyle, THEME2_ADMIN_INSETS } from "hooks/useTheme2FormPage";

const SITES_BASE_DOMAIN = process.env.REACT_APP_SITES_BASE_DOMAIN || "dealertiq.com";

const WebsiteSettings = ({ activeColor = "blue", account, tenantId, shellThemeVariant = "1" }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const {
        inputStyle,
        inputClassName,
        theme2FieldsCss,
        contentClassName,
        formFieldsClassName,
    } = useTheme2FormPage({
        shellThemeVariant,
        activeColorStyle,
        pageKey: "website-settings",
        insetClass: THEME2_ADMIN_INSETS.tenants,
        withSelectStyles: false,
    });

    const updateTenantMutation = useUpdateTenant();

    const [webEnabled, setWebEnabled] = useState(false);
    const [subdomain, setSubdomain] = useState("");
    const [customDomain, setCustomDomain] = useState("");
    const [inventoryPublishAfterDays, setInventoryPublishAfterDays] = useState(0);

    useEffect(() => {
        setWebEnabled(account?.web_enabled === true);
        setSubdomain(
            account?.dealer_site?.subdomain ||
            account?.web?.subdomain ||
            account?.subdomain ||
            ""
        );
        setCustomDomain(account?.dealer_site?.custom_domain || "");
        setInventoryPublishAfterDays(
            Number(account?.dealer_site?.inventory_publish_after_days || 0)
        );
    }, [account]);

    const handleSave = (e) => {
        e.preventDefault();

        const id = tenantId || account?.code || account?.id;
        if (!id) return;

        const web = {
            web_enabled: webEnabled,
            custom_domain: customDomain.trim(),
            inventory_publish_after_days: Number(inventoryPublishAfterDays) || 0,
        };

        updateTenantMutation.mutate(
            { id, data: { web } },
            {
                onSuccess: async () => {
                    await queryClient.invalidateQueries({ queryKey: ["tenant", id] });
                },
            }
        );
    };

    return (
        <div className={contentClassName}>
            <Theme2FormFieldsStyle css={theme2FieldsCss} />
            <style>
                {`
          .website-settings-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .website-settings-card-header h2,
          .website-settings-card-header h4 {
            font-size: 16px !important;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .website-settings-page .website-settings-card-header > .d-flex {
            margin-top: 3px;
          }
          .website-settings-page .card .card-body {
            padding-top: 8px !important;
            padding-bottom: 8px !important;
            padding-left: 15px;
            padding-right: 15px;
          }
        `}
            </style>
            <Row>
                <Col md="12">
                    <Card>
                        <CardHeader className="website-settings-card-header">
                            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
                                <h2 className="mb-0">{t("websiteSettings.title", "Web Site Settings")}</h2>

                                <div className="d-none d-lg-flex align-items-center">
                                    <Button
                                        color="primary"
                                        size="sm"
                                        type="submit"
                                        form="website-settings-form"
                                        style={{ ...activeColorStyle, color: "white" }}
                                        disabled={updateTenantMutation.isPending}
                                    >
                                        {updateTenantMutation.isPending ? (
                                            <>
                                                <Spinner size="sm" className="mr-1" />
                                                {t("common.updating", "Updating...")}
                                            </>
                                        ) : (
                                            <>
                                                <i className="tim-icons icon-check-2 mr-1" />
                                                {t("common.update", "Update")}
                                            </>
                                        )}
                                    </Button>
                                </div>

                                <div className="d-flex d-lg-none flex-column w-100 mt-3">
                                    <Button
                                        color="primary"
                                        type="submit"
                                        form="website-settings-form"
                                        style={{ ...activeColorStyle, color: "white", padding: "12px" }}
                                        disabled={updateTenantMutation.isPending}
                                        className="w-100"
                                    >
                                        {updateTenantMutation.isPending
                                            ? t("common.updating", "Updating...")
                                            : t("common.update", "Update")}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <form
                                id="website-settings-form"
                                onSubmit={handleSave}
                                className={formFieldsClassName}
                            >
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className="d-block mb-2">
                                                {t("websiteSettings.webEnabled", "Web Site Enabled")}
                                            </Label>
                                            <CustomInput
                                                type="switch"
                                                id="web_enabled"
                                                name="web_enabled"
                                                label={
                                                    webEnabled
                                                        ? t("common.enabled", "Enabled")
                                                        : t("common.disabled", "Disabled")
                                                }
                                                checked={webEnabled}
                                                onChange={(e) => setWebEnabled(e.target.checked)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                {webEnabled && (
                                    <>
                                        <Row>
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label>{t("websiteSettings.subdomain", "Subdomain")}</Label>
                                                    <div className="d-flex align-items-center">
                                                        <Input
                                                            name="subdomain"
                                                            type="text"
                                                            readOnly
                                                            disabled
                                                            className={inputClassName()}
                                                            style={inputStyle}
                                                            value={subdomain}
                                                        />
                                                        <span className="ml-2 text-muted">.{SITES_BASE_DOMAIN}</span>
                                                    </div>
                                                    <small className="text-muted">
                                                        {t(
                                                            "websiteSettings.subdomainReadOnly",
                                                            "Subdomain is assigned when the website is enabled and cannot be changed here."
                                                        )}
                                                    </small>
                                                </FormGroup>
                                            </Col>
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label>{t("websiteSettings.customDomain", "Custom Domain")}</Label>
                                                    <Input
                                                        name="custom_domain"
                                                        type="text"
                                                        placeholder="www.example.com"
                                                        className={inputClassName()}
                                                        style={inputStyle}
                                                        value={customDomain}
                                                        onChange={(e) => setCustomDomain(e.target.value)}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label>
                                                        {t(
                                                            "websiteSettings.inventoryPublishAfterDays",
                                                            "Publish Inventory After (days)"
                                                        )}
                                                    </Label>
                                                    <Input
                                                        name="inventory_publish_after_days"
                                                        type="number"
                                                        min="0"
                                                        step="1"
                                                        className={inputClassName()}
                                                        style={inputStyle}
                                                        value={inventoryPublishAfterDays}
                                                        onChange={(e) =>
                                                            setInventoryPublishAfterDays(e.target.value)
                                                        }
                                                    />
                                                    <small className="text-muted">
                                                        {t(
                                                            "websiteSettings.inventoryPublishAfterDaysHelp",
                                                            "Vehicles appear on the public site only after this many days in inventory."
                                                        )}
                                                    </small>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default WebsiteSettings;
